import Cookies, { CookieAttributes } from "js-cookie";

export const BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL;
const COOKIE_NAME_ACCESS_TOKEN = "access_token";

export const COOKIE_OPTIONS_ACCESS_TOKEN: CookieAttributes = {
  // it is not possible to deliberately set `httpOnly` to true
  //  - HttpOnly can only be set to true by the browser at the behest of the server:
  //  - a request's response must contain the header Set-Cookie with the HttpOnly flag set to true
  //  - https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie#httponly
  // it is not possible to set a cookie's `domain` attribute to something other than the current domain
  //  - https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie#domaindomain-value
  //  - NOTE: a server-side cookie would set the domain to the server's domain
  //
  // NOTE: since we are deliberately setting the `Authorization` header in the request with the `access_token`'s value,
  //       `sameSite`/`secure` are set as good measure to tell the browser how to [not-]use this cookie
  //       `sameSite`/`secure` have no effect on our application code that deliberately sets ths `Authorization` header
  sameSite: "strict",
  secure: true,
};

export const setCookieAccessToken = (access_token: string): void => {
  Cookies.set(
    COOKIE_NAME_ACCESS_TOKEN,
    access_token,
    COOKIE_OPTIONS_ACCESS_TOKEN,
  );
};

export const removeCookieAccessToken = (): void => {
  Cookies.remove(COOKIE_NAME_ACCESS_TOKEN, COOKIE_OPTIONS_ACCESS_TOKEN);
};

export const getCookieAccessToken = (): string | undefined =>
  Cookies.get(COOKIE_NAME_ACCESS_TOKEN);

let cachedBaseUrl: string = "";
let healthCheckPromise: Promise<Response> | null = null;

/**
 * Retrieves the base URL for the backend service.
 *
 * This function checks if a cached base URL is available and returns it.
 * If not, it performs a health check on the new backend base URL.
 * If the health check is successful, it caches and returns the new base URL.
 * Otherwise, it falls back to the default base URL.
 *
 * @returns {Promise<string>} The base URL for the backend service.
 */
export const getBaseUrl = async (): Promise<string> => {
  if (cachedBaseUrl) {
    return cachedBaseUrl;
  }

  if (!healthCheckPromise) {
    const newBaseUrl = import.meta.env.VITE_NEW_BACKEND_BASE_URL;
    if (newBaseUrl) {
      healthCheckPromise = Promise.race([
        fetch(`${newBaseUrl}/api/v1/health/status/`, {
          method: "GET",
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Timeout")), 5000),
        ),
      ]).finally(() => {
        healthCheckPromise = null;
      });
    }
  }

  try {
    const response = await healthCheckPromise;
    // If we get any response whatsoever, we consider the new backend to be reachable
    if (response) {
      cachedBaseUrl = import.meta.env.VITE_NEW_BACKEND_BASE_URL || BASE_URL;
      return cachedBaseUrl;
    }
  } catch {
    // Swallow any errors
  }

  cachedBaseUrl = BASE_URL;
  return BASE_URL;
};
